// Shared portal panels: Overview, Notes, Tasks, Files.
// These are rendered inside either the cream ClientView or dark TeamView.
import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, Clock, FileText, Image as ImageIcon, Paperclip, Plus, Send, Trash2, Upload, X } from "lucide-react";
import { PORTAL_API as API, formatApiError } from "./PortalAuth";

const dt = (s) => (s ? new Date(s).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "");
const date = (s) => (s ? new Date(s).toLocaleDateString(undefined, { dateStyle: "medium" }) : "");

const initials = (name) =>
  (name || "?").split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();

// ─── A small "tone" prop selects palette: "light" (cream) or "dark"
const tones = {
  light: {
    panel: "bg-cream border border-ink/15",
    card: "bg-white border border-ink/10",
    text: "text-ink",
    mute: "text-ink/65",
    sub: "text-ink/45",
    chip: "border-ink/25 text-ink",
    chipOn: "bg-ink text-cream border-ink",
    btn: "bg-ink text-cream border-ink hover:bg-cream hover:text-ink",
    input: "bg-transparent text-ink border-ink/25 focus:border-ink placeholder:text-ink/40",
    rule: "border-ink/15",
    accent: "text-ink",
    softHover: "hover:bg-ink/5",
  },
  dark: {
    panel: "bg-jet/70 border border-cream/15",
    card: "bg-jet border border-cream/10",
    text: "text-cream",
    mute: "text-cream/65",
    sub: "text-cream/45",
    chip: "border-cream/25 text-cream",
    chipOn: "bg-gold text-jet border-gold",
    btn: "bg-gold text-jet border-gold hover:bg-cream hover:text-jet",
    input: "bg-transparent text-cream border-cream/25 focus:border-gold placeholder:text-cream/35",
    rule: "border-cream/15",
    accent: "text-gold",
    softHover: "hover:bg-cream/5",
  },
};

// ────────────────────────────── OVERVIEW ──────────────────────────────
export function OverviewPanel({ client, currentUser, tone = "light", onSaved }) {
  const t = tones[tone];
  const canEdit = currentUser.role !== "client" || client.user_id === currentUser.id;
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    display_name: client.display_name || "",
    tagline: client.tagline || "",
    bio: client.bio || "",
    brand_url: client.brand_url || "",
    contract_status: client.contract_status || "",
  });

  async function save() {
    try {
      const { data } = await axios.patch(`${API}/clients/${client.id}`, form, { withCredentials: true });
      toast.success("Saved.");
      onSaved?.(data);
      setEditing(false);
    } catch (e) { toast.error(formatApiError(e)); }
  }

  return (
    <div className={`${t.panel} p-6 md:p-8`} data-testid="panel-overview">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <div className={`overline ${t.sub}`}>Overview</div>
          <h3 className={`font-display uppercase text-3xl md:text-5xl leading-[0.9] mt-1 ${t.text}`}>{client.display_name}</h3>
          {client.tagline && <p className={`font-serif italic text-lg ${t.mute} mt-2`}>{client.tagline}</p>}
        </div>
        {canEdit && !editing && (
          <button
            onClick={() => setEditing(true)}
            className={`overline border px-3 py-1.5 ${t.chip} ${t.softHover} transition`}
            data-testid="overview-edit-btn"
          >
            Edit profile
          </button>
        )}
      </div>

      {!editing ? (
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <div className={`overline ${t.sub} mb-1`}>Bio</div>
            <p className={`${t.mute} leading-relaxed whitespace-pre-line`}>{client.bio || <span className={t.sub}>No bio yet.</span>}</p>
          </div>
          <dl className="space-y-4 text-sm">
            <Pair t={t} label="Contract" value={client.contract_status} />
            <Pair t={t} label="Brand URL" value={client.brand_url} link />
            <Pair t={t} label="Member since" value={date(client.created_at)} />
            {client.user?.last_login && <Pair t={t} label="Last sign-in" value={dt(client.user.last_login)} />}
          </dl>
        </div>
      ) : (
        <div className="space-y-5">
          <Field t={t} label="Display name" value={form.display_name} onChange={(v) => setForm({ ...form, display_name: v })} />
          <Field t={t} label="Tagline" value={form.tagline} onChange={(v) => setForm({ ...form, tagline: v })} />
          <Field t={t} label="Bio" value={form.bio} onChange={(v) => setForm({ ...form, bio: v })} textarea />
          <Field t={t} label="Brand URL" value={form.brand_url} onChange={(v) => setForm({ ...form, brand_url: v })} />
          <Field t={t} label="Contract status" value={form.contract_status} onChange={(v) => setForm({ ...form, contract_status: v })} />
          <div className="flex gap-3 pt-2">
            <button onClick={save} className={`overline border px-4 py-2.5 ${t.btn}`} data-testid="overview-save-btn">Save</button>
            <button onClick={() => setEditing(false)} className={`overline border px-4 py-2.5 ${t.chip} ${t.softHover}`}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

function Pair({ t, label, value, link }) {
  if (!value) return null;
  return (
    <div>
      <dt className={`overline ${t.sub}`}>{label}</dt>
      <dd className={`mt-1 ${t.text}`}>
        {link ? <a className="underline" href={value} target="_blank" rel="noreferrer">{value}</a> : value}
      </dd>
    </div>
  );
}

function Field({ t, label, value, onChange, textarea }) {
  return (
    <div>
      <label className={`overline ${t.sub}`}>{label}</label>
      {textarea ? (
        <textarea
          value={value} onChange={(e) => onChange(e.target.value)}
          rows={4}
          className={`w-full mt-1 px-3 py-2 border-b outline-none ${t.input}`}
        />
      ) : (
        <input
          value={value} onChange={(e) => onChange(e.target.value)}
          className={`w-full mt-1 px-3 py-2 border-b outline-none ${t.input}`}
        />
      )}
    </div>
  );
}

// ────────────────────────────── NOTES (threaded messages) ──────────────────────────────
export function NotesPanel({ client, currentUser, tone = "light" }) {
  const t = tones[tone];
  const [notes, setNotes] = useState([]);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const scroller = useRef(null);

  async function load() {
    try {
      const { data } = await axios.get(`${API}/clients/${client.id}/notes`, { withCredentials: true });
      setNotes(data);
    } catch (e) { toast.error(formatApiError(e)); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [client.id]);

  async function send() {
    if (!body.trim()) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/clients/${client.id}/notes`, { body: body.trim() }, { withCredentials: true });
      setNotes([data, ...notes]);
      setBody("");
      setTimeout(() => scroller.current?.scrollTo({ top: 0, behavior: "smooth" }), 50);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  }

  return (
    <div className={`${t.panel} flex flex-col h-[640px]`} data-testid="panel-notes">
      <div className="px-6 pt-5 pb-4 border-b border-current/10 flex items-baseline justify-between">
        <div>
          <div className={`overline ${t.sub}`}>Notes &amp; Messages</div>
          <h3 className={`font-display uppercase text-2xl md:text-3xl mt-1 ${t.text}`}>The thread</h3>
        </div>
        <span className={`overline ${t.sub}`}>{notes.length} message{notes.length !== 1 ? "s" : ""}</span>
      </div>

      <div ref={scroller} className="flex-1 overflow-y-auto p-6 space-y-5">
        {notes.length === 0 && (
          <p className={`italic text-center ${t.sub} py-12`}>No messages yet. Start the conversation below.</p>
        )}
        {notes.map((n) => {
          const mine = n.author?.id === currentUser.id;
          const fromTeam = n.author?.role === "team" || n.author?.role === "super_admin";
          return (
            <div key={n.id} className={`flex gap-3 ${mine ? "flex-row-reverse" : ""}`}>
              <div className={`h-9 w-9 rounded-full flex items-center justify-center text-xs tracking-wider shrink-0 ${
                fromTeam ? "bg-gold text-jet" : t.tone === "dark" ? "bg-cream/15 text-cream" : "bg-ink text-cream"
              }`}>{initials(n.author?.name)}</div>
              <div className={`max-w-[78%] ${mine ? "text-right" : ""}`}>
                <div className={`text-xs ${t.sub} mb-1`}>
                  {n.author?.name || "Someone"} · <span className="italic">{fromTeam ? "Team" : "Client"}</span> · {dt(n.created_at)}
                </div>
                <div className={`inline-block whitespace-pre-wrap leading-relaxed ${t.card} px-4 py-3 ${t.text}`}>
                  {n.body}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className={`border-t ${t.rule} p-4 flex gap-3`}>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) send(); }}
          placeholder="Write a note…  (⌘/Ctrl + Enter to send)"
          rows={2}
          className={`flex-1 px-3 py-2 border-b outline-none ${t.input}`}
          data-testid="notes-input"
        />
        <button onClick={send} disabled={busy || !body.trim()} className={`overline border self-end px-4 py-2.5 disabled:opacity-50 ${t.btn}`} data-testid="notes-send-btn">
          <Send size={14} className="inline mr-1.5" />Send
        </button>
      </div>
    </div>
  );
}

// ────────────────────────────── TASKS ──────────────────────────────
export function TasksPanel({ client, currentUser, tone = "light" }) {
  const t = tones[tone];
  const isTeam = currentUser.role === "super_admin" || currentUser.role === "team";
  const [tasks, setTasks] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", due_date: "", assignee_role: "client" });

  async function load() {
    try {
      const { data } = await axios.get(`${API}/clients/${client.id}/tasks`, { withCredentials: true });
      setTasks(data);
    } catch (e) { toast.error(formatApiError(e)); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [client.id]);

  async function create() {
    if (!form.title.trim()) return;
    try {
      const payload = { ...form, due_date: form.due_date || null };
      const { data } = await axios.post(`${API}/clients/${client.id}/tasks`, payload, { withCredentials: true });
      setTasks([data, ...tasks]);
      setForm({ title: "", description: "", due_date: "", assignee_role: "client" });
      setShowNew(false);
    } catch (e) { toast.error(formatApiError(e)); }
  }
  async function toggleStatus(task) {
    const next = task.status === "done" ? "todo" : "done";
    try {
      const { data } = await axios.patch(`${API}/tasks/${task.id}`, { status: next }, { withCredentials: true });
      setTasks(tasks.map((x) => (x.id === task.id ? data : x)));
    } catch (e) { toast.error(formatApiError(e)); }
  }
  async function remove(task) {
    if (!isTeam) return;
    try {
      await axios.delete(`${API}/tasks/${task.id}`, { withCredentials: true });
      setTasks(tasks.filter((x) => x.id !== task.id));
    } catch (e) { toast.error(formatApiError(e)); }
  }

  const grouped = useMemo(() => ({
    todo: tasks.filter((x) => x.status !== "done"),
    done: tasks.filter((x) => x.status === "done"),
  }), [tasks]);

  return (
    <div className={`${t.panel} p-6 md:p-7`} data-testid="panel-tasks">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <div className={`overline ${t.sub}`}>Tasks &amp; To-Do</div>
          <h3 className={`font-display uppercase text-2xl md:text-3xl mt-1 ${t.text}`}>The list</h3>
        </div>
        {isTeam && (
          <button onClick={() => setShowNew(!showNew)} className={`overline border px-3 py-1.5 ${t.chip} ${t.softHover}`} data-testid="task-new-btn">
            <Plus size={14} className="inline mr-1" />New task
          </button>
        )}
      </div>

      {showNew && (
        <div className={`${t.card} p-4 mb-5 space-y-3`}>
          <Field t={t} label="Title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} />
          <Field t={t} label="Description" value={form.description} onChange={(v) => setForm({ ...form, description: v })} textarea />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={`overline ${t.sub}`}>Due date</label>
              <input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                className={`w-full mt-1 px-3 py-2 border-b outline-none ${t.input}`} />
            </div>
            <div>
              <label className={`overline ${t.sub}`}>Assignee</label>
              <select value={form.assignee_role} onChange={(e) => setForm({ ...form, assignee_role: e.target.value })}
                className={`w-full mt-1 px-3 py-2 border-b outline-none bg-transparent ${t.text} ${t.input}`}>
                <option value="client" className={tone === "dark" ? "bg-jet" : ""}>Client</option>
                <option value="team" className={tone === "dark" ? "bg-jet" : ""}>Team</option>
              </select>
            </div>
          </div>
          <div className="flex gap-2 pt-1">
            <button onClick={create} className={`overline border px-4 py-2 ${t.btn}`} data-testid="task-create-btn">Create</button>
            <button onClick={() => setShowNew(false)} className={`overline border px-4 py-2 ${t.chip} ${t.softHover}`}>Cancel</button>
          </div>
        </div>
      )}

      <Section title="Open" tone={t} count={grouped.todo.length}>
        {grouped.todo.length === 0 ? (
          <p className={`italic ${t.sub} py-4`}>Nothing on the list.</p>
        ) : grouped.todo.map((task) => (
          <TaskRow key={task.id} task={task} t={t} onToggle={() => toggleStatus(task)} onDelete={isTeam ? () => remove(task) : null} />
        ))}
      </Section>

      <Section title="Completed" tone={t} count={grouped.done.length}>
        {grouped.done.length === 0 ? (
          <p className={`italic ${t.sub} py-4`}>None yet.</p>
        ) : grouped.done.map((task) => (
          <TaskRow key={task.id} task={task} t={t} onToggle={() => toggleStatus(task)} onDelete={isTeam ? () => remove(task) : null} done />
        ))}
      </Section>
    </div>
  );
}

function Section({ title, count, tone, children }) {
  return (
    <div className="mt-5">
      <div className="flex items-baseline justify-between">
        <div className={`overline ${tone.sub}`}>{title}</div>
        <div className={`overline ${tone.sub}`}>{count}</div>
      </div>
      <div className={`mt-3 border-t ${tone.rule} divide-y ${tone.rule.replace("border", "divide")}`}>{children}</div>
    </div>
  );
}

function TaskRow({ task, t, onToggle, onDelete, done }) {
  const overdue = task.due_date && new Date(task.due_date) < new Date() && task.status !== "done";
  return (
    <div className="flex items-start gap-3 py-3" data-testid={`task-${task.id}`}>
      <button onClick={onToggle} className={`mt-1 h-5 w-5 border flex items-center justify-center transition ${done ? `bg-gold border-gold` : t.rule}`} aria-label="Toggle">
        {done && <Check size={12} className="text-jet" strokeWidth={3} />}
      </button>
      <div className="flex-1">
        <div className={`${done ? "line-through " + t.sub : t.text} font-medium`}>{task.title}</div>
        {task.description && <p className={`text-sm ${t.mute} mt-1 whitespace-pre-line`}>{task.description}</p>}
        <div className={`mt-1 text-xs ${t.sub} flex flex-wrap gap-x-3 gap-y-1`}>
          <span>For: <span className="italic">{task.assignee_role}</span></span>
          {task.due_date && (
            <span className={overdue ? "text-carpet font-medium" : ""}>
              <Clock size={11} className="inline mr-1" />Due {date(task.due_date)}{overdue ? " · overdue" : ""}
            </span>
          )}
        </div>
      </div>
      {onDelete && (
        <button onClick={onDelete} className={`p-1.5 ${t.sub} hover:text-carpet transition`} aria-label="Delete">
          <Trash2 size={14} />
        </button>
      )}
    </div>
  );
}

// ────────────────────────────── FILES ──────────────────────────────
export function FilesPanel({ client, currentUser, tone = "light" }) {
  const t = tones[tone];
  const isTeam = currentUser.role === "super_admin" || currentUser.role === "team";
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [category, setCategory] = useState("work");
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  async function load() {
    try {
      const { data } = await axios.get(`${API}/clients/${client.id}/files`, { withCredentials: true });
      setFiles(data);
    } catch (e) { toast.error(formatApiError(e)); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [client.id]);

  async function upload(fileList) {
    const items = Array.from(fileList || []);
    if (!items.length) return;
    setUploading(true);
    try {
      for (const f of items) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await axios.post(`${API}/clients/${client.id}/files?category=${encodeURIComponent(category)}`, fd, {
          withCredentials: true,
          headers: { "Content-Type": "multipart/form-data" },
        });
        setFiles((cur) => [data, ...cur]);
      }
      toast.success(items.length === 1 ? "Uploaded." : `${items.length} files uploaded.`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUploading(false); }
  }

  async function remove(file) {
    if (!isTeam) return;
    if (!window.confirm(`Delete ${file.original_filename}?`)) return;
    try {
      await axios.delete(`${API}/files/${file.id}`, { withCredentials: true });
      setFiles(files.filter((x) => x.id !== file.id));
    } catch (e) { toast.error(formatApiError(e)); }
  }

  function onDrop(e) {
    e.preventDefault(); setDragOver(false);
    upload(e.dataTransfer.files);
  }

  return (
    <div className={`${t.panel} p-6 md:p-7`} data-testid="panel-files">
      <div className="flex items-baseline justify-between flex-wrap gap-3 mb-5">
        <div>
          <div className={`overline ${t.sub}`}>Files &amp; Uploads</div>
          <h3 className={`font-display uppercase text-2xl md:text-3xl mt-1 ${t.text}`}>The vault</h3>
        </div>
        <div className="flex items-center gap-2">
          <select value={category} onChange={(e) => setCategory(e.target.value)} className={`overline border px-3 py-1.5 bg-transparent ${t.chip} ${t.softHover}`}>
            <option value="work" className={tone === "dark" ? "bg-jet" : ""}>Work</option>
            <option value="headshot" className={tone === "dark" ? "bg-jet" : ""}>Headshot</option>
            <option value="asset" className={tone === "dark" ? "bg-jet" : ""}>Asset</option>
            <option value="approval" className={tone === "dark" ? "bg-jet" : ""}>For Approval</option>
            <option value="reference" className={tone === "dark" ? "bg-jet" : ""}>Reference</option>
          </select>
        </div>
      </div>

      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={`border-2 border-dashed ${dragOver ? "border-gold" : t.rule} p-8 text-center transition-colors cursor-pointer ${t.softHover}`}
        onClick={() => inputRef.current?.click()}
        data-testid="files-dropzone"
      >
        <Upload size={28} className={`mx-auto ${t.mute} mb-2`} />
        <div className={`${t.text} font-medium`}>{uploading ? "Uploading…" : "Drop files here or click to browse"}</div>
        <div className={`${t.sub} text-xs mt-1`}>Up to 25 MB · {category}</div>
        <input ref={inputRef} type="file" multiple hidden onChange={(e) => upload(e.target.files)} data-testid="files-input" />
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        {files.length === 0 && <p className={`italic ${t.sub} col-span-full py-6`}>No files yet.</p>}
        {files.map((f) => (
          <FileRow key={f.id} file={f} tone={tone} onDelete={isTeam ? () => remove(f) : null} />
        ))}
      </div>
    </div>
  );
}

function FileRow({ file, tone, onDelete }) {
  const t = tones[tone];
  const isImage = (file.content_type || "").startsWith("image/");
  const href = `${API}/files/${file.id}`;
  return (
    <div className={`${t.card} p-4 flex gap-4 items-center`} data-testid={`file-${file.id}`}>
      <div className={`h-14 w-14 shrink-0 flex items-center justify-center ${tone === "dark" ? "bg-cream/10" : "bg-ink/5"}`}>
        {isImage ? <ImageIcon size={22} className={t.mute} /> : <FileText size={22} className={t.mute} />}
      </div>
      <div className="flex-1 min-w-0">
        <a href={href} target="_blank" rel="noreferrer" className={`block truncate ${t.text} hover:underline`}>{file.original_filename}</a>
        <div className={`text-xs ${t.sub} mt-1 flex flex-wrap gap-x-2`}>
          <span className="capitalize">{file.category}</span>
          <span>·</span>
          <span>{(file.size / 1024 / 1024).toFixed(2)} MB</span>
          <span>·</span>
          <span>by {file.uploader?.name || "someone"}</span>
          <span>·</span>
          <span>{dt(file.created_at)}</span>
        </div>
      </div>
      {onDelete && (
        <button onClick={onDelete} className={`p-1.5 ${t.sub} hover:text-carpet transition`} aria-label="Delete">
          <Trash2 size={14} />
        </button>
      )}
    </div>
  );
}
